/**
 * Colour roles for the Warm-up world figures (Juno's desk). Storybook (A) values are hand-picked
 * (warm, slightly muted, ART-BIBLE §5.3); Sticker (B) and Pastel (C) are derived by `rolePalette`.
 * Shared roles (cream, pink, eye, blush, leaf, water, sparkle, white…) come from the base palette.
 */
import { rolePalette } from '../../art/palette';

export const INTRO_PALETTE = rolePalette({
  // sun
  introSun: '#F7C948',
  introSunRay: '#F29A3E',
  // snake
  introSnake: '#8CC46A',
  introSnakeBand: '#F2C14E',
  introSnakeDot: '#4E8A5C',
  introSnakeBelly: '#E8F2C8',
  // house
  introWall: '#F6E1BE',
  introRoof: '#D9604F',
  introRoofDark: '#B24A3F',
  introDoor: '#8A5A3C',
  introWindow: '#9FD4E6',
  introFrame: '#FFF8EC',
  introChimney: '#B8794F',
  introGrass: '#9CCB72',
  introPath: '#E8D2A8',
  // cocoa
  introMug: '#E5675A',
  introMugDark: '#B9473F',
  introCocoa: '#8A5236',
  introMarsh: '#FCE3EA',
  introSaucer: '#BFE0DC',
  introSteam: '#D8CDBD',
  introCookie: '#E2A866',
  // kite
  introKiteBlue: '#4C86C9',
  introKiteYellow: '#F2C14E',
  introSpar: '#A8734E',
  introBow: '#E5675A',
  introString: '#8E7B6B',
  // sailboat
  introSail: '#FFF4E2',
  introSailStripe: '#E5675A',
  introHull: '#4C86C9',
  introHullDark: '#3A6BA6',
  introMast: '#A8734E',
  introFlag: '#F2C14E',
  introSea: '#7CC3CF',
  introSeaDark: '#5AA7B8',
  introFoam: '#E9F6F4',
  // ball
  introBall: '#E9675B',
  introBallBand: '#FFF1DC',
  introBallDark: '#B44A42',
  introCast: '#C9A77E',
  // desk (ball table, lamp, masterpiece)
  introDesk: '#E9C99A',
  introDeskDark: '#B98A5A',
  // rocket
  introRocket: '#F3ECE2',
  introNose: '#E5675A',
  introFin: '#4C86C9',
  introPort: '#8FCFE0',
  introRim: '#B7C2CE',
  introFlame: '#F59A3B',
  introFlameCore: '#F7D35C',
  // star
  introStar: '#F7C948',
  introTrail: '#F29A3E',
  // pencil doodle
  introLead: '#5B4A44',
  // alarm clock
  introClock: '#4FA3A5',
  introClockDark: '#2F7778',
  introBell: '#F2B544',
  // hot-air balloon
  introEnvelope: '#E86A5C',
  introGore: '#F7CF5A',
  introBasket: '#C98E5E',
  introBasketDark: '#8E6040',
  introSandbag: '#E6D6BC',
  // desk lamp
  introLamp: '#3F9C9A',
  introLampDark: '#2C716F',
  introBulb: '#FFF0B8',
  introGlow: '#FFE38A',
  introPaper: '#FFFDF6',
  // pattern gym tiles
  introTileA: '#FCE3EA',
  introTileB: '#DDEEF7',
  introTileC: '#E8F2C8',
  introTileD: '#FFF1C9',
  introTileE: '#EADCF4',
  introTileF: '#FFE1CC',
  // light gym
  introSphere: '#F29A3E',
  introCube: '#5B93D1',
  introCubeTop: '#9CC2EC',
  introCubeSide: '#355F99',
  introCyl: '#8CC46A',
  introCylTop: '#C9E6AE',
  // balloon bunch
  introBunchA: '#F28AA0',
  introBunchB: '#7CC3CF',
  introBunchC: '#F7C948',
  // shoe
  introShoe: '#E5675A',
  introSole: '#FFF4E2',
  introLace: '#FFFFFF',
  introToe: '#F3ECE2',
  // then & now sun
  introSky: '#FFE9C7',
  introSunDeep: '#F08A3A',
  // sun remixes
  introShades: '#2F4858',
  introNightcap: '#7A8FD0',
  introPetal: '#F48FB1',
  introPetalB: '#F9B98A',
  introPetalVein: '#D9688E',
  // house remixes
  introFrosting: '#F4A3BE',
  introMint: '#BFE6D2',
  introTrunk: '#8A5A3C',
  introMoss: '#9CC77A',
  introCap: '#D9533F',
  introCapDark: '#A93B2E',
  // kite remixes
  introWing: '#B79AE3',
  introWingB: '#F7C948',
  introWingDark: '#5E4A86',
  introOcto: '#EE8FB6',
  introOctoLight: '#FBD3E3',
  // boat remixes
  introPaperBoat: '#FBF6EA',
  introNewsprint: '#E4DED2',
  introNewsDark: '#8C8478',
  introPatchA: '#9AD3BF',
  introPatchB: '#F7CF5A',
  introPatchC: '#F4A3BE',
  introNet: '#D9C7A6',
  introFishGold: '#F59A3B',
  // rocket remixes
  introSand: '#EED7A6',
  introSeaweed: '#5E9E6A',
  // ball remixes
  introGlobe: '#D6ECF6',
  introGlobeBase: '#8A5A3C',
  introBubble: '#F3F7FB',
  introSheenPink: '#F4A3BE',
  introSheenBlue: '#7CC3E6',
  introSheenGold: '#F7D35C',
  // scenes
  introWallpaper: '#F6E3C8',
  introWallLine: '#E6CBA6',
  introMorning: '#CFE8F2',
  introHillFar: '#B9D9A8',
  introFrameDark: '#D9C3A0',
  introCurtain: '#EE9B8C',
  introSkyDay: '#D5ECF5',
  introNight: '#3E4A86',
  introMoon: '#FFF1C1',
  // crossovers
  introWrapper: '#F28AA0',
  introWrapperDark: '#C9667C',
  introCandle: '#9AD3BF',
  introCherry: '#C9364A',
  introFishFin: '#F7B267',
  introFishDark: '#C46A22',
  introCream: '#FFF1DC',
  introShirt: '#F3ECE2',
  introShirtStripe: '#4C86C9',
  introBandana: '#D9483B',
  introFlagDark: '#3B3F5C',
  introParrot: '#6FB86A',
  introParrotWing: '#4E8FD1',
  introChibiHair: '#5B3A33',
  introJacket: '#7FB86A',
  introJacketDark: '#4F7FB8',
  introGoggle: '#C2513F',
});
